import React, { useState, useEffect, useCallback, memo } from 'react';
import { Play, Clock } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import Footer from '../Components/Footer';
import { useTVNavigation } from '../hooks/useTVNavigation';

// ─── API BASE ─────────────────────────────────────────────────────
const API_BASE = 'https://dolphin-app-33jp4.ondigitalocean.app/api/movie_streaming';

async function fetchSection(endpoint) {
  try {
    const r = await fetch(`${API_BASE}/${endpoint}`);
    if (!r.ok) return [];
    const data = await r.json();
    return Array.isArray(data) ? data : [];
  } catch {
    return [];
  }
}

// ─── REUSABLE: Horizontal scroll row ─────────────────────────────
const Row = memo(({ title, subtitle, items, renderCard, accent = '#14b8a6' }) => {
  if (!items || !items.length) return null;
  return (
    <section style={{ padding: 'clamp(14px,2vw,28px) 0 4px' }}>
      <div style={{ padding: '0 clamp(16px,3vw,40px)', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', marginBottom: 'clamp(8px,1vw,14px)' }}>
        <div>
          <h2 style={{ color: '#f1f5f9', fontSize: 'clamp(13px,1.4vw,19px)', fontWeight: 700, margin: 0, letterSpacing: '-0.3px' }}>{title}</h2>
          {subtitle && <p style={{ color: 'rgba(255,255,255,0.35)', fontSize: 'clamp(9px,0.9vw,12px)', margin: '3px 0 0' }}>{subtitle}</p>}
        </div>
        <button style={{ color: accent, fontSize: 'clamp(9px,0.9vw,12px)', fontWeight: 600, background: 'none', border: 'none', cursor: 'pointer', opacity: 0.8 }}>
          See All →
        </button>
      </div>
      <div data-tv-row style={{ display: 'flex', gap: 'clamp(8px,1vw,12px)', overflowX: 'auto', padding: 'clamp(4px,0.5vw,4px) clamp(16px,3vw,40px) 16px', scrollbarWidth: 'none', msOverflowStyle: 'none' }}>
        {items.map((item, i) => renderCard(item, i))}
      </div>
    </section>
  );
});

// ─── REUSABLE: Portrait card (2:3) ───────────────────────────────
const PortraitCard = memo(({ item, rankBadge, accentGlow = '#14b8a6', onWatch }) => (
  <div
    data-tv-card
    tabIndex={0}
    onClick={() => onWatch(item)}
    style={{ flexShrink: 0, width: 'clamp(90px,10vw,140px)', cursor: 'pointer', outline: 'none', borderRadius: 10, transition: 'transform .2s, box-shadow .2s' }}
    onFocus={e  => { e.currentTarget.style.transform = 'scale(1.07)'; e.currentTarget.style.boxShadow = `0 0 0 3px ${accentGlow}, 0 12px 28px rgba(0,0,0,0.6)`; }}
    onBlur={e   => { e.currentTarget.style.transform = 'scale(1)';    e.currentTarget.style.boxShadow = 'none'; }}
    onMouseEnter={e => { e.currentTarget.style.transform = 'scale(1.05)'; }}
    onMouseLeave={e => { e.currentTarget.style.transform = 'scale(1)'; }}
  >
    <div style={{ position: 'relative', borderRadius: 10, overflow: 'hidden', aspectRatio: '2/3' }}>
      <img
        src={item.image}
        alt={item.title}
        decoding="async"
        style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
        loading="lazy"
        onError={e => { e.target.src = 'https://images.unsplash.com/photo-1478720568477-152d9b164e26?w=280&h=400&fit=crop'; }}
      />
      <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(to top,rgba(0,0,0,0.85) 0%,transparent 55%)' }} />
      {rankBadge != null && (
        <div style={{ position: 'absolute', bottom: 8, left: 8, fontSize: 'clamp(24px,3vw,40px)', fontWeight: 900, color: 'rgba(255,255,255,0.13)', lineHeight: 1 }}>
          {rankBadge}
        </div>
      )}
      {item.rating != null && (
        <div style={{ position: 'absolute', top: 7, right: 7, background: 'rgba(0,0,0,0.65)', color: '#fbbf24', fontSize: 'clamp(8px,0.8vw,10px)', fontWeight: 700, padding: '3px 6px', borderRadius: 5 }}>
          ★ {item.rating}
        </div>
      )}
      {item.duration && (
        <div style={{ position: 'absolute', bottom: 7, left: 7, display: 'flex', alignItems: 'center', gap: 3 }}>
          <Clock size={9} color="rgba(255,255,255,0.5)" />
          <span style={{ color: 'rgba(255,255,255,0.5)', fontSize: 'clamp(7px,0.7vw,9px)' }}>{item.duration}</span>
        </div>
      )}
      {item.seasons != null && (
        <div style={{ position: 'absolute', bottom: 7, left: 7, background: 'rgba(0,0,0,0.6)', color: 'rgba(255,255,255,0.7)', fontSize: 'clamp(7px,0.7vw,9px)', padding: '2px 6px', borderRadius: 4 }}>
          {item.seasons} Season{item.seasons > 1 ? 's' : ''}
        </div>
      )}
    </div>
    <p style={{ color: '#f1f5f9', fontSize: 'clamp(9px,0.9vw,12px)', fontWeight: 600, margin: '6px 0 2px', lineHeight: 1.35, overflow: 'hidden', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical' }}>
      {item.title}
    </p>
  </div>
));

// ─── REUSABLE: Landscape card (16:9) ─────────────────────────────
const LandscapeCard = memo(({ item, isLive = false, onWatch }) => (
  <div
    data-tv-card
    tabIndex={0}
    onClick={() => onWatch(item)}
    style={{ flexShrink: 0, width: 'clamp(140px,15vw,210px)', cursor: 'pointer', outline: 'none', borderRadius: 10, transition: 'transform .2s, box-shadow .2s' }}
    onFocus={e  => { e.currentTarget.style.transform = 'scale(1.04)'; e.currentTarget.style.boxShadow = `0 0 0 3px ${isLive ? '#ef4444' : '#14b8a6'}, 0 10px 24px rgba(0,0,0,0.6)`; }}
    onBlur={e   => { e.currentTarget.style.transform = 'scale(1)';    e.currentTarget.style.boxShadow = 'none'; }}
    onMouseEnter={e => { e.currentTarget.style.transform = 'scale(1.04)'; }}
    onMouseLeave={e => { e.currentTarget.style.transform = 'scale(1)'; }}
  >
    <div style={{ position: 'relative', borderRadius: 10, overflow: 'hidden', height: 'clamp(80px,9vw,130px)' }}>
      <img
        src={item.image}
        alt={item.title}
        decoding="async"
        style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
        loading="lazy"
        onError={e => { e.target.src = 'https://images.unsplash.com/photo-1598899134739-24c46f58b8c0?w=400&h=250&fit=crop'; }}
      />
      <div style={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.4)' }} />
      {isLive && (
        <div style={{ position: 'absolute', top: 8, left: 8, display: 'flex', alignItems: 'center', gap: 5, background: '#ef4444', padding: '3px 8px', borderRadius: 5 }}>
          <span style={{ width: 6, height: 6, borderRadius: '50%', background: '#fff', display: 'block', animation: 'livePulse 1.2s ease-in-out infinite' }} />
          <span style={{ color: '#fff', fontSize: 'clamp(7px,0.7vw,9px)', fontWeight: 800, letterSpacing: '0.08em' }}>LIVE</span>
        </div>
      )}
      {item.viewers && (
        <div style={{ position: 'absolute', top: 8, right: 8, background: 'rgba(0,0,0,0.65)', color: 'rgba(255,255,255,0.85)', fontSize: 'clamp(8px,0.8vw,10px)', fontWeight: 600, padding: '3px 8px', borderRadius: 5 }}>
          👁 {item.viewers}
        </div>
      )}
      <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <div style={{ width: 'clamp(28px,3vw,40px)', height: 'clamp(28px,3vw,40px)', borderRadius: '50%', background: isLive ? 'rgba(239,68,68,0.85)' : 'rgba(255,255,255,0.9)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <Play size={14} color={isLive ? '#fff' : '#000'} fill={isLive ? '#fff' : '#000'} />
        </div>
      </div>
      {item.progress != null && (
        <div style={{ position: 'absolute', bottom: 0, left: 0, right: 0, height: 3, background: 'rgba(255,255,255,0.15)' }}>
          <div style={{ height: '100%', width: `${item.progress}%`, background: '#14b8a6' }} />
        </div>
      )}
    </div>
    <p style={{ color: '#f1f5f9', fontSize: 'clamp(9px,0.9vw,12px)', fontWeight: 600, margin: '6px 0 2px', overflow: 'hidden', whiteSpace: 'nowrap', textOverflow: 'ellipsis' }}>
      {item.title}
    </p>
    {item.progress != null && (
      <p style={{ color: 'rgba(255,255,255,0.38)', fontSize: 'clamp(8px,0.8vw,10px)', margin: 0 }}>{item.progress}% watched</p>
    )}
  </div>
));

// ─── MAIN COMPONENT ──────────────────────────────────────────────
const Home = () => {
  const navigate = useNavigate();

  const [sections, setSections] = useState({
    heroSlides: [], trending: [], continueWatch: [], top10: [],
    watchlist: [], liveTV: [], recentAdded: [], imdbPicks: [],
    genres: [], movies: [], series: [],
  });
  const [loading,      setLoading]      = useState(true);
  const [currentSlide, setCurrentSlide] = useState(0);

  const { heroSlides, trending, continueWatch, top10,
          watchlist, liveTV, recentAdded, imdbPicks,
          genres, movies, series } = sections;

  useEffect(() => {
    (async () => {
      setLoading(true);
      const [h, tr, cw, t10, wl, lt, ra, ip, gn, mv, sr] = await Promise.all([
        fetchSection('hero_slides'),
        fetchSection('latest_trending'),
        fetchSection('continue_watching'),
        fetchSection('top_10_india'),
        fetchSection('watchlist'),
        fetchSection('live_tv'),
        fetchSection('recently_added'),
        fetchSection('imdb_top_picks'),
        fetchSection('genres'),
        fetchSection('movies'),
        fetchSection('series'),
      ]);
      setSections({
        heroSlides: h, trending: tr, continueWatch: cw, top10: t10,
        watchlist: wl, liveTV: lt, recentAdded: ra, imdbPicks: ip,
        genres: gn, movies: mv, series: sr,
      });
      setLoading(false);
    })();
  }, []);

  useEffect(() => {
    if (!heroSlides.length) return;
    const t = setInterval(() => setCurrentSlide(p => (p + 1) % heroSlides.length), 6000);
    return () => clearInterval(t);
  }, [heroSlides]);

  useTVNavigation({ enabled: true });

  const handleWatch = useCallback((item) => {
    const url = item.video_url || item.stream_url || '';
    navigate(`/watch/movie/${item.id}`, { state: { item, url } });
  }, [navigate]);

  if (loading) return (
    <div style={{ minHeight: '100vh', background: '#080d1c', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
      <div style={{ textAlign: 'center' }}>
        <div style={{ width: 48, height: 48, border: '3px solid #1e293b', borderTopColor: '#14b8a6', borderRadius: '50%', animation: 'spin .8s linear infinite', margin: '0 auto 16px' }} />
        <p style={{ color: 'rgba(255,255,255,0.4)', fontSize: 14, margin: 0 }}>Loading...</p>
      </div>
    </div>
  );

  const slide = heroSlides[currentSlide];

  return (
    <div style={{ minHeight: '100vh', background: '#080d1c', color: '#fff', fontFamily: "'Segoe UI','SF Pro Text',-apple-system,sans-serif" }}>
      <style>{`
        [data-tv-row]::-webkit-scrollbar { display: none; }
        @keyframes fadeUp    { from { opacity:0; transform:translateY(22px); } to { opacity:1; transform:none; } }
        @keyframes livePulse { 0%,100% { opacity:1; transform:scale(1); } 50% { opacity:.5; transform:scale(.8); } }
        [data-tv-card]:focus { outline: 3px solid #14b8a6 !important; outline-offset: 2px; transform: scale(1.07); box-shadow: 0 0 0 4px rgba(20,184,166,0.3), 0 12px 30px rgba(0,0,0,0.6) !important; }
        [data-tv-nav]:focus  { outline: 2px solid #14b8a6 !important; outline-offset: 2px; border-radius: 4px; }
        [data-tv-card] img   { will-change: auto; }
        [data-tv-row]        { contain: layout style; }
      `}</style>

      {/* ══════ HERO ══════ */}
      {slide && (
        <section style={{ position: 'relative', height: 'clamp(55vh,72vh,88vh)', overflow: 'hidden' }}>

          {heroSlides.map((s, i) => {
            const dist = Math.abs(i - currentSlide);
            if (dist > 1) return null;
            return (
              <div key={s.id} style={{ position: 'absolute', inset: 0, opacity: i === currentSlide ? 1 : 0, transition: 'opacity 1.2s ease', pointerEvents: i === currentSlide ? 'auto' : 'none' }}>
                <img
                  src={s.image}
                  alt={s.title}
                  fetchpriority={i === currentSlide ? 'high' : 'low'}
                  decoding="async"
                  style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover' }}
                  onError={e => { e.target.src = 'https://images.unsplash.com/photo-1478720568477-152d9b164e26?w=1280&h=720&fit=crop'; }}
                />
                <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(to right,rgba(8,13,28,0.97) 0%,rgba(8,13,28,0.7) 45%,rgba(8,13,28,0.05) 100%)' }} />
                <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(to top,rgba(8,13,28,1) 0%,transparent 45%)' }} />
              </div>
            );
          })}

          {/* Hero content */}
          <div style={{ position: 'relative', zIndex: 2, height: '100%', display: 'flex', alignItems: 'center', padding: '0 clamp(24px,4vw,60px)', paddingTop: 'clamp(60px,8vh,100px)' }}>
            <div style={{ maxWidth: 'clamp(320px,45vw,580px)', animation: 'fadeUp .7s ease both' }}>

              {/* genre chips */}
              {slide.genre && (
                <div style={{ display: 'flex', gap: 'clamp(4px,0.6vw,8px)', flexWrap: 'wrap', marginBottom: 'clamp(8px,1vw,14px)' }}>
                  {slide.genre.split(',').map(g => (
                    <span key={g} style={{ background: 'rgba(255,255,255,0.08)', color: 'rgba(255,255,255,0.6)', fontSize: 'clamp(8px,0.8vw,10px)', fontWeight: 600, padding: '3px 8px', borderRadius: 20, border: '1px solid rgba(255,255,255,0.1)' }}>
                      {g.trim()}
                    </span>
                  ))}
                </div>
              )}

              {/* title */}
              <h1 style={{ color: '#fff', fontSize: 'clamp(22px,3.5vw,46px)', fontWeight: 900, letterSpacing: '-1.5px', lineHeight: 1.05, margin: '0 0 clamp(8px,1vw,14px)', textTransform: 'uppercase', textShadow: '0 2px 24px rgba(0,0,0,0.5)' }}>
                {slide.title}
              </h1>

              {/* rating · duration · starring */}
              <div style={{ display: 'flex', alignItems: 'center', gap: 'clamp(6px,1vw,12px)', marginBottom: 'clamp(8px,1vw,14px)', flexWrap: 'wrap' }}>
                {slide.rating != null && (
                  <span style={{ color: '#fbbf24', fontSize: 'clamp(10px,1vw,14px)', fontWeight: 700 }}>★ {slide.rating}</span>
                )}
                {slide.duration && (
                  <><span style={{ color: 'rgba(255,255,255,0.3)' }}>|</span>
                  <span style={{ color: 'rgba(255,255,255,0.6)', fontSize: 'clamp(10px,1vw,13px)' }}>⏱ {slide.duration}</span></>
                )}
                {slide.starring && (
                  <><span style={{ color: 'rgba(255,255,255,0.3)' }}>|</span>
                  <span style={{ color: 'rgba(255,255,255,0.55)', fontSize: 'clamp(9px,0.9vw,12px)' }}>🎬 {slide.starring}</span></>
                )}
              </div>

              {/* description */}
              {slide.description && (
                <p style={{ color: 'rgba(255,255,255,0.6)', fontSize: 'clamp(10px,1vw,14px)', lineHeight: 1.7, margin: '0 0 clamp(16px,2vw,28px)', maxWidth: 480 }}>
                  {slide.description}
                </p>
              )}

              {/* CTA buttons */}
              <div data-tv-row style={{ display: 'flex', gap: 'clamp(8px,1vw,12px)' }}>
                <button
                  data-tv-card
                  tabIndex={0}
                  onClick={() => handleWatch(slide)}
                  style={{ display: 'flex', alignItems: 'center', gap: 'clamp(5px,0.6vw,8px)', background: '#fff', color: '#080d1c', padding: 'clamp(8px,1vw,13px) clamp(16px,2vw,30px)', borderRadius: 8, fontWeight: 800, fontSize: 'clamp(11px,1.1vw,15px)', border: 'none', cursor: 'pointer' }}
                >
                  <Play size={16} fill="#080d1c" /> Watch Now
                </button>
                <button
                  data-tv-card
                  tabIndex={0}
                  style={{ display: 'flex', alignItems: 'center', gap: 'clamp(5px,0.6vw,8px)', background: 'rgba(255,255,255,0.1)', color: '#fff', padding: 'clamp(8px,1vw,13px) clamp(14px,1.8vw,24px)', borderRadius: 8, fontWeight: 700, fontSize: 'clamp(10px,1vw,14px)', border: '1px solid rgba(255,255,255,0.2)', cursor: 'pointer', backdropFilter: 'blur(6px)' }}
                >
                  + Watchlist
                </button>
              </div>
            </div>
          </div>

          {/* Dots */}
          <div style={{ position: 'absolute', bottom: 'clamp(14px,2vh,28px)', right: 'clamp(24px,4vw,60px)', display: 'flex', gap: 6, zIndex: 3 }}>
            {heroSlides.map((_, i) => (
              <button key={i} onClick={() => setCurrentSlide(i)}
                style={{ width: i === currentSlide ? 24 : 6, height: 6, borderRadius: 3, background: i === currentSlide ? '#14b8a6' : 'rgba(255,255,255,0.2)', border: 'none', cursor: 'pointer', transition: 'all .3s', padding: 0 }} />
            ))}
          </div>
        </section>
      )}

      {/* ── Rows ── */}
      <Row title="🔥 Latest Trending" subtitle="Hot right now" items={trending} accent="#ef4444"
        renderCard={(item) => <PortraitCard key={item.id} item={item} accentGlow="#ef4444" onWatch={handleWatch} />}
      />

      <Row title="▶ Continue Watching" subtitle="Pick up where you left off" items={continueWatch} accent="#14b8a6"
        renderCard={(item) => <LandscapeCard key={item.id} item={item} onWatch={handleWatch} />}
      />

      <Row title="🇮🇳 Top 10 in India" subtitle="Most watched today" items={top10} accent="#f59e0b"
        renderCard={(item, idx) => <PortraitCard key={item.id} item={item} rankBadge={item.rank || idx + 1} accentGlow="#f59e0b" onWatch={handleWatch} />}
      />

      <Row title="📡 Live TV" subtitle="Streaming right now" items={liveTV} accent="#ef4444"
        renderCard={(item) => <LandscapeCard key={item.id} item={item} isLive onWatch={handleWatch} />}
      />

      <Row title="✨ Recently Added" subtitle="Fresh content this week" items={recentAdded} accent="#6366f1"
        renderCard={(item) => <PortraitCard key={item.id} item={item} accentGlow="#6366f1" onWatch={handleWatch} />}
      />

      <Row title="⭐ IMDb Top Picks" subtitle="Critically acclaimed worldwide" items={imdbPicks} accent="#f59e0b"
        renderCard={(item, idx) => <PortraitCard key={item.id} item={item} rankBadge={idx + 1} accentGlow="#f59e0b" onWatch={handleWatch} />}
      />

      <Row title="🔖 My Watchlist" subtitle="Saved to watch later" items={watchlist} accent="#14b8a6"
        renderCard={(item) => <PortraitCard key={item.id} item={item} onWatch={handleWatch} />}
      />

      <Row title="🎬 Movies" subtitle="Watch full movies" items={movies} accent="#14b8a6"
        renderCard={(item) => <PortraitCard key={item.id} item={item} onWatch={handleWatch} />}
      />

      <Row title="📺 Series" subtitle="Binge-worthy shows" items={series} accent="#8b5cf6"
        renderCard={(item) => <PortraitCard key={item.id} item={item} accentGlow="#8b5cf6" onWatch={handleWatch} />}
      />

      {/* ── Genre grid ── */}
      {genres.length > 0 && (
        <section style={{ padding: 'clamp(14px,2vw,28px) 0 16px' }}>
          <div style={{ padding: '0 clamp(16px,3vw,40px)', marginBottom: 'clamp(8px,1vw,14px)' }}>
            <h2 style={{ color: '#f1f5f9', fontSize: 'clamp(13px,1.4vw,19px)', fontWeight: 700, margin: 0 }}>🎭 Browse by Genre</h2>
            <p style={{ color: 'rgba(255,255,255,0.35)', fontSize: 'clamp(9px,0.9vw,12px)', margin: '3px 0 0' }}>Find something you love</p>
          </div>
          <div data-tv-row style={{ display: 'flex', gap: 'clamp(8px,1vw,12px)', overflowX: 'auto', padding: '4px clamp(16px,3vw,40px) 16px', scrollbarWidth: 'none' }}>
            {genres.map(g => (
              <div key={g.id} data-tv-card tabIndex={0}
                style={{ flexShrink: 0, width: 'clamp(120px,14vw,200px)', height: 'clamp(70px,7vw,110px)', cursor: 'pointer', outline: 'none', borderRadius: 12, overflow: 'hidden', position: 'relative', transition: 'transform .2s, box-shadow .2s' }}
                onFocus={e  => { e.currentTarget.style.transform = 'scale(1.05)'; e.currentTarget.style.boxShadow = '0 0 0 3px #14b8a6'; }}
                onBlur={e   => { e.currentTarget.style.transform = 'scale(1)';    e.currentTarget.style.boxShadow = 'none'; }}
                onMouseEnter={e => { e.currentTarget.style.transform = 'scale(1.05)'; }}
                onMouseLeave={e => { e.currentTarget.style.transform = 'scale(1)'; }}
              >
                <img src={g.image} alt={g.name} style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover' }} loading="lazy"
                  onError={e => { e.target.src = 'https://images.unsplash.com/photo-1489599849927-2ee91cede3ba?w=400&h=250&fit=crop'; }} />
                <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(135deg,rgba(0,0,0,0.55),rgba(0,0,0,0.2))' }} />
                <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'flex-end', padding: 'clamp(8px,1vw,14px) clamp(10px,1.2vw,16px)' }}>
                  <p style={{ color: '#fff', fontSize: 'clamp(11px,1.1vw,16px)', fontWeight: 800, margin: 0, letterSpacing: '0.06em', textShadow: '0 2px 8px rgba(0,0,0,0.6)' }}>
                    {g.name}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </section>
      )}

      <Footer />
    </div>
  );
};

export default Home;